/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */

import { css } from '@compiled/react';
import classnames from 'classnames';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { expValEquals } from '@confluence/feature-experiments';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';

import { BylineAppTriggerVisual } from './BylineAppTriggerVisual.tsx';

const imageStyles = css({
	maxHeight: '100%',
	height: 'auto',
	display: 'inline-block',
	verticalAlign: 'middle',
	marginRight: token('space.0'),
});
const legacyImageSpacingStyles = css({
	marginRight: '5px',
});
// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
const baseStyles = css({ marginRight: token('space.100'), lineHeight: '1.5' });
const largeMargin = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	marginRight: `${token('space.250')} !important`,
	// CCECO-4179 - Leaving this line-height here since it is specific to web items, their icons, and icons coming from 3rd party apps.
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography, @atlaskit/ui-styling-standard/no-important-styles
	lineHeight: '2 !important',
});

const bylineStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	color: `${token('color.text.subtle')} !important`,
});

const badgeStyles = css({
	borderRadius: token('radius.small'),
	display: 'flex',
	gap: token('space.050'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	marginRight: token('space.0'),
	padding: token('space.050'),
	textDecoration: 'none',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		textDecorationLine: 'none !important',
	},
	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
		textDecorationLine: 'none',
	},
});

const sharedBadgeLinkStyles = css({
	display: 'inline-flex',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'none',
	},
	'&:active': {
		textDecoration: 'none',
	},
});

const WebItemTooltip = ({ tooltip, children }) => {
	if (tooltip) {
		return <Tooltip content={tooltip}>{(tooltipProps) => children(tooltipProps)}</Tooltip>;
	}

	return children({});
};

WebItemTooltip.propTypes = {
	/**
	 * Tooltip of the web item
	 */
	tooltip: PropTypes.string,
	children: PropTypes.func.isRequired,
};

const WebItemIcon = ({
	classes,
	id,
	url,
	label,
	tooltip,
	onClick,
	style,
	icon,
	addLargerMargin,
	useBylineStyles,
	useBadgeStyles,
	hasAuthoritativeBylineAppSnapshot,
	...otherProps
}) => {
	const content = (
		<>
			{icon}
			{typeof label === 'string' ? <span dangerouslySetInnerHTML={{ __html: label }} /> : label}
		</>
	);

	return (
		<WebItemTooltip tooltip={tooltip}>
			{(tooltipProps) => {
				const linkProps = {
					'data-testid': 'web-item-link',
					href: url,
					id,
					onClick,
					style,
					...tooltipProps,
					...otherProps,
				};

				const shouldUseSharedBylineAppTrigger =
					useBadgeStyles &&
					hasAuthoritativeBylineAppSnapshot &&
					expValEquals('confluence_ssr_byline', 'isEnabled', true);

				return (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a
						{...linkProps}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={classes}
						css={[
							shouldUseSharedBylineAppTrigger && sharedBadgeLinkStyles,
							!shouldUseSharedBylineAppTrigger && baseStyles,
							!shouldUseSharedBylineAppTrigger && addLargerMargin && largeMargin,
							!shouldUseSharedBylineAppTrigger && useBylineStyles && bylineStyles,
							!shouldUseSharedBylineAppTrigger && useBadgeStyles && badgeStyles,
						]}
					>
						{shouldUseSharedBylineAppTrigger ? (
							<BylineAppTriggerVisual isInteractive>{content}</BylineAppTriggerVisual>
						) : (
							content
						)}
					</a>
				);
			}}
		</WebItemTooltip>
	);
};

WebItemIcon.propTypes = {
	/**
	 * classes to use for the component
	 */
	classes: PropTypes.string,
	/**
	 * id passed from WebItem
	 */
	id: PropTypes.string,
	/**
	 * Route where the web item will point
	 */
	url: PropTypes.string,
	/**
	 * Label of the web item
	 */
	label: PropTypes.string,
	/**
	 * Tooltip of the web item
	 */
	tooltip: PropTypes.string,
	/**
	 * Handler for clicks
	 */
	onClick: PropTypes.func,
	/**
	 * WebItem styles
	 */
	style: PropTypes.object,
	/**
	 * Icon obj
	 */
	icon: PropTypes.object,
	/**
	 * Will be true for live pages since apps have different styling
	 */
	addLargerMargin: PropTypes.bool,
	/**
	 * Will be true for live pages since apps have different styling
	 */
	useBylineStyles: PropTypes.bool,
	useBadgeStyles: PropTypes.bool,
	hasAuthoritativeBylineAppSnapshot: PropTypes.bool,
};

export class WebItem extends PureComponent {
	static propTypes = {
		/**
		 * Classnames. Note that WebItem no longer assumes that it should render as a button
		 * And as such you may need to explicitly pass the `aui-button` class here.
		 */
		className: PropTypes.string,
		/**
		 * id
		 */
		id: PropTypes.string,
		/**
		 * Route where the web item will point
		 */
		url: PropTypes.string,
		/**
		 * Type of the button
		 */
		type: PropTypes.string,
		/**
		 * Label of the web item
		 */
		label: PropTypes.string,
		/**
		 * Tooltip of the web item
		 */
		tooltip: PropTypes.string,
		/**
		 * Handler for clicks
		 */
		onClick: PropTypes.func,
		/**
		 * Contain parameters setted in the descriptor
		 */
		params: PropTypes.object,
		/**
		 * Doesn't render icons when true
		 */
		disableIcon: PropTypes.bool,
		/**
		 * icon information used for absolute images
		 */
		iconInfo: PropTypes.shape({
			path: PropTypes.string,
			width: PropTypes.number,
			height: PropTypes.number,
		}),
		/**
		 * Show spinner instead of an icon if equals true
		 */
		spinner: PropTypes.bool,
		/**
		 * WebItem styles
		 */
		style: PropTypes.object,
		/**
		 * Icon styles
		 */
		iconStyle: PropTypes.object,
		iconSize: PropTypes.string,
		/**
		 * Will be true for live pages since apps have different styling
		 */
		addLargerMargin: PropTypes.bool,
		/**
		 * Will be true for live pages since apps have different styling
		 */
		useBylineStyles: PropTypes.bool,
		useBadgeStyles: PropTypes.bool,
		hasAuthoritativeBylineAppSnapshot: PropTypes.bool,
	};

	static defaultProps = {
		spinner: false,
		iconStyle: {},
		style: {},
		iconSize: 'small',
	};

	getIcon() {
		const {
			disableIcon,
			params,
			iconStyle,
			spinner,
			iconInfo,
			label,
			iconSize,
			useBadgeStyles,
			hasAuthoritativeBylineAppSnapshot,
		} = this.props;
		let auiIconClass = params && params.iconClass;

		if (disableIcon) {
			return null;
		}

		if (spinner) {
			return <Spinner testId="spinner" size="small" />;
		}

		if (iconInfo && iconInfo.path) {
			return (
				<img
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={iconStyle}
					css={[
						imageStyles,
						(!useBadgeStyles ||
							!hasAuthoritativeBylineAppSnapshot ||
							!expValEquals('confluence_ssr_byline', 'isEnabled', true)) &&
							legacyImageSpacingStyles,
					]}
					src={iconInfo.path}
					width={iconInfo.width}
					height={iconInfo.height}
					alt={label}
					title={label}
				/>
			);
		}

		if (auiIconClass) {
			if (auiIconClass.indexOf('aui-iconfont-') === -1) {
				auiIconClass = `aui-iconfont-${auiIconClass}`;
			}
			return (
				<ContentIcon
					iconSize={iconSize}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={auiIconClass}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={iconStyle}
				/>
			);
		}

		return null;
	}

	render() {
		const { className, id, url, type, label, tooltip, onClick, style, ...otherProps } = this.props;
		const buttonType = type === 'primary' ? 'aui-button-primary' : '';
		const classes = classnames(className, buttonType);
		const icon = this.getIcon();
		//react complains about these extra props but we still need to pass otherProps (e.g. data-jira-metadata-count)
		const webItemIconProps = omit(
			{
				classes,
				id,
				url,
				label,
				tooltip,
				onClick,
				style,
				icon,
				...otherProps,
			},
			['disableIcon', 'iconStyle', 'spinner', 'iconInfo', 'iconSize'],
		);

		return <WebItemIcon {...webItemIconProps} />;
	}
}
